import React, { useEffect, useState } from "react";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { useResource, useFetcher } from "rest-hooks";
import { ConsentResource, LoginResource } from "../../../resources";
import { useInvalidateOnUnmount } from "../../../utils";
import { useSnackbar } from "notistack";
import { FormattedMessage } from "react-intl";
import { makeStyles } from "@material-ui/styles";
import HelpOutlineOutlinedIcon from "@material-ui/icons/HelpOutlineOutlined";
import HelpIcon from '@material-ui/icons/Help';
import { Theme } from "@material-ui/core";
import { useCommonWidths } from "../../../hooks";
import MuiSwitch from "@material-ui/core/Switch";
import { SnackbarContentWrapper, MyPagePanel } from "../../common";
import { MyConsentsProps } from "./MyConsents";
import { navigate } from "@reach/router";
import { v4 } from "uuid";

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		maxWidth: 900,
	},
	contentWrapper: {
		paddingTop: theme.spacing(8),
		paddingBottom: theme.spacing(8),
	},
	helpIconButton: {
		marginLeft: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			marginLeft: theme.spacing(1)
		},
		paddingTop: 4,
	},
	consentListItemWrapper: {
		borderBottom: "solid 1px lightgrey",
		padding: "2rem 0",
		"&:first-child": {
			paddingTop: (reduceMargins: boolean) => reduceMargins ? "0" : "2rem"
		}
	},
	consentControllerRow: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
	},
	consentLabel: {
		fontWeight: "bold",
	},
	requiredOrOptionalInfo: {
		fontStyle: "italic",
	},
	mobileDescription: {
		marginTop: theme.spacing(1),
	},
}));

interface ConsentFormProps extends MyConsentsProps {
	onChange?: (e: any, val: boolean) => void;
	consentsFromProps?: ConsentResource[];
	externalState?: Record<string, "true" | "false">;
	renderDuringOnboarding?: boolean;
}

const ConsentForm = (props: ConsentFormProps) => {
	const { onChange, consentsFromProps, externalState, renderDuringOnboarding = false } = props;

	const consents = consentsFromProps ?? useResource(ConsentResource.listShape(), {});
	useInvalidateOnUnmount(ConsentResource.listShape(), {});

	const loginStatus = useFetcher(LoginResource.statusShape());
	useEffect(() => {
		async function fetchLoginStatus() {
			const { isAuthenticated } = await loginStatus({});
		}
		fetchLoginStatus();
	}, [loginStatus]);

	const classes = useStyles(renderDuringOnboarding);
	if (consents.length === 0) return <div></div>;

	return (
		<MyPagePanel className={classes.root} reduceMargins={renderDuringOnboarding}>
			<Box display="flex" flexDirection="column">
				{consents.map((consent: ConsentResource) => (
					<PrivacyConsentItem
						onChangeConsentCallback={onChange}
						key={consent.id}
						consent={consent}
						classes={classes}
						checked={
							externalState
								? externalState[consent.id] === "true"
								: consent.consented
						}
					/>
				))}
			</Box>
		</MyPagePanel>
	);
};

interface PrivacyConsentItemProps {
	consent: ConsentResource;
	classes: any;
	onChangeConsentCallback?: (e: any, val: boolean) => void;
	checked: boolean;
}
const PrivacyConsentItem = ({
	consent,
	classes,
	onChangeConsentCallback,
	checked,
}: PrivacyConsentItemProps) => {
	const update = useFetcher(ConsentResource.updateShape());

	const isMobile = useCommonWidths().isSmDown;

	const { enqueueSnackbar } = useSnackbar();

	const [showDescription, setShowDescription] = useState(false);
	const [state, setState] = useState<boolean>(checked);

	const onConsentChange = (evt: any, checked: boolean) => {
		setState(!state);
		if (onChangeConsentCallback) {
			return onChangeConsentCallback(evt, checked);
		}
		update({}, { id: consent.id, consented: checked })
			.then(() => {
				enqueueSnackbar("", {
					key: `${consent.id}_${v4()}`,
					autoHideDuration: 1500,
					content: (key) => (
						<SnackbarContentWrapper
							variant="success"
							message={
								<FormattedMessage
									id="minside.consentform.success_message"
									defaultMessage="Samtykke oppdatert"
									description="Successfull consent update"
								/>
							}
							id={key}
						/>
					),
				});

				if (consent.id === "ecommunications" && checked === false) {
					enqueueSnackbar("", {
						key: `${consent.id}_${v4()}`,
						autoHideDuration: 10000,
						content: (key) => (
							<SnackbarContentWrapper
								variant="warning"
								message={
									<FormattedMessage
										id="minside.consentform.ecommunications_warning"
										defaultMessage="Hvis du ønsker tilgang til MinSide må du samtykke til digital kommunikasjon."
										description="Successfull consent update"
									/>
								}
								id={key}
							/>
						),
					});
				}
			})
			.catch((err) => {
				if (err.toString().includes("Unauthorized")) {
					enqueueSnackbar("", {
						key: `${consent.id}_${v4()}`,
						content: (key: any) => (
							<SnackbarContentWrapper
								variant="warning"
								message={
									<FormattedMessage
										id="minside.consentform.unauth_message"
										defaultMessage="Sesjonen har utløpt. Vennligst logg inn på nytt"
										description="Session expired"
									/>
								}
								id={key}
							/>
						),
					});
					navigate("/logout", { state: { errorCode: 401 } });
				} else {
					enqueueSnackbar("", {
						key: `${consent.id}_${v4()}`,
						autoHideDuration: 4000,
						content: (key) => (
							<SnackbarContentWrapper
								variant="error"
								message={
									<FormattedMessage
										id="minside.consentform.error_message"
										defaultMessage="En feil har oppstått"
										description="Error on consent update"
									/>
								}
								id={key}
							/>
						),
					});
				}
			});
	};

	return (
		<div className={classes.consentListItemWrapper}>
			<Box className={classes.consentControllerRow}>
				<Grid container wrap="nowrap" spacing={0}>
					<Grid item xs={11}>
						<Typography component="div" className={classes.consentLabel}>
							{consent.label}
						</Typography>
						<Typography
							component="div"
							className={classes.requiredOrOptionalInfo}
						>
							{consent.id === "ecommunications" ? "* Påkrevd" : "(Valgfri)"}
						</Typography>
						{!isMobile && (
							<Typography component="div" color="textSecondary">
								{consent.description}
							</Typography>
						)}
					</Grid>
				</Grid>
				<Box>
					<Grid
						component="label"
						wrap="nowrap"
						container
						alignItems="center"
						spacing={0}
					>
						<Grid item>
							<Typography variant="overline" color="textSecondary">
								<FormattedMessage
									id="minside.consentform.no"
									defaultMessage="Nei"
									description="Consent label [no/disagree]"
								/>
							</Typography>
						</Grid>
						<Grid item>
							<MuiSwitch
								onChange={onConsentChange}
								id={consent.id}
								checked={state}
							/>
						</Grid>
						<Grid item>
							<Typography variant="overline" color="textSecondary">
								<FormattedMessage
									id="minside.consentform.yes"
									defaultMessage="Ja"
									description="Consent label [yes/agree]"
								/>
							</Typography>
						</Grid>
					</Grid>
				</Box>
				{isMobile && (
					<Grid item className={classes.helpIconButton}>
						{showDescription
							? <HelpIcon onClick={() => setShowDescription(false)} />
							: <HelpOutlineOutlinedIcon onClick={() => setShowDescription(true)}/>
						}
					</Grid>
				)}
			</Box>
			{isMobile && showDescription && (
				<Typography
					component="div"
					className={classes.mobileDescription}
				>
					{consent.description}
				</Typography>
			)}
		</div>
	);
};

export default ConsentForm;
